import React, { useState, useEffect, useCallback } from "react";
import { apiService } from "../services/api";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import toast from "react-hot-toast";
import {
  MagnifyingGlassIcon,
  PencilIcon,
  EyeIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  MapPinIcon,
  CalendarIcon,
  CreditCardIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

const ClubManagement = () => {
  const [clubs, setClubs] = useState([]);
  const [filteredClubs, setFilteredClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClub, setSelectedClub] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClubForm, setNewClubForm] = useState({
    name: "",
    location: "",
    adminEmail: "",
    adminName: "",
    accessCode: "",
    subscriptionPlanId: "",
    billingStartDate: "",
    billingEndDate: "",
    autoRenew: true,
  });

  useEffect(() => {
    loadClubs();
    loadSubscriptionPlans();
  }, []);

  const filterClubs = useCallback(() => {
    if (!searchTerm) {
      setFilteredClubs(clubs);
      return;
    }

    const filtered = clubs.filter((club) => {
      const searchLower = searchTerm.toLowerCase();
      const clubName = club.name?.toLowerCase() || "";
      const clubLocation = club.location?.toLowerCase() || "";
      const adminNames = club.users
        ?.filter((user) => user.role === "club_admin")
        .map((admin) => admin.name?.toLowerCase() || "")
        .join(" ");

      return (
        clubName.includes(searchLower) ||
        clubLocation.includes(searchLower) ||
        adminNames.includes(searchLower)
      );
    });

    setFilteredClubs(filtered);
  }, [searchTerm, clubs]);

  useEffect(() => {
    filterClubs();
  }, [filterClubs]);

  const loadClubs = async () => {
    try {
      setLoading(true);
      const clubsData = await apiService.getAllClubs();
      setClubs(clubsData);
    } catch (error) {
      console.error("Failed to load clubs:", error);
      toast.error("Failed to load clubs");
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionPlans = async () => {
    try {
      const plans = await apiService.getSubscriptionPlans();
      setSubscriptionPlans(plans);
    } catch (error) {
      console.error("Failed to load subscription plans:", error);
    }
  };

  const handleViewDetails = (club) => {
    setSelectedClub(club);
    setShowDetailsModal(true);
  };

  const handleOpenClub = (club) => {
    window.open(`/portal/club/${club.id}/dashboard`, "_blank");
  };

  const handleEditClub = (club) => {
    setEditingClub({
      ...club,
      subscriptionPlanId: club.subscription_plan_id || "",
      billingStartDate: club.billing_start_date || "",
      billingEndDate: club.billing_end_date || "",
      autoRenew: club.auto_renew !== undefined ? club.auto_renew : true,
    });
    setShowEditModal(true);
  };

  const handleUpdateClub = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const updates = {
        name: editingClub.name,
        location: editingClub.location,
      };

      await apiService.updateClub(editingClub.id, updates);

      // If subscription plan changed, update it
      if (editingClub.subscriptionPlanId !== editingClub.subscription_plan_id) {
        try {
          await apiService.assignSubscriptionToClub(editingClub.id, {
            subscriptionPlanId: editingClub.subscriptionPlanId || null,
            startDate: editingClub.billingStartDate,
            endDate: editingClub.billingEndDate,
            autoRenew: editingClub.autoRenew,
          });
        } catch (subscriptionError) {
          toast.error("Club updated but failed to update subscription plan");
        }
      }

      setShowEditModal(false);
      setEditingClub(null);
      toast.success("Club updated successfully!");
      loadClubs(); // Reload to get updated data
    } catch (error) {
      toast.error("Failed to update club");
    }

    setUpdating(false);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;

    // If subscription plan is being changed and a plan is selected, auto-set billing dates
    if (
      name === "subscriptionPlanId" &&
      value &&
      value !== editingClub.subscription_plan_id
    ) {
      const today = new Date();
      const oneYearFromToday = new Date(today);
      oneYearFromToday.setFullYear(today.getFullYear() + 1);

      setEditingClub({
        ...editingClub,
        [name]: value,
        billingStartDate: today.toISOString().split("T")[0],
        billingEndDate: oneYearFromToday.toISOString().split("T")[0],
      });
    } else {
      setEditingClub({
        ...editingClub,
        [name]: value,
      });
    }
  };

  const generateAccessCode = () => {
    const code = Math.random().toString(36).slice(2, 8).toUpperCase();
    setNewClubForm((prev) => ({ ...prev, accessCode: code }));
  };

  const handleCreateInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    if (
      name === "subscriptionPlanId" &&
      value &&
      value !== newClubForm.subscriptionPlanId
    ) {
      const today = new Date();
      const oneYearFromToday = new Date(today);
      oneYearFromToday.setFullYear(today.getFullYear() + 1);
      setNewClubForm((prev) => ({
        ...prev,
        subscriptionPlanId: value,
        billingStartDate: today.toISOString().split("T")[0],
        billingEndDate: oneYearFromToday.toISOString().split("T")[0],
      }));
    } else {
      setNewClubForm((prev) => ({ ...prev, [name]: newValue }));
    }
  };

  const handleCreateClub = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      await apiService.createClub({
        name: newClubForm.name,
        location: newClubForm.location,
        adminEmail: newClubForm.adminEmail,
        adminName: newClubForm.adminName,
        accessCode: newClubForm.accessCode,
        subscriptionPlanId: newClubForm.subscriptionPlanId || undefined,
        billingStartDate: newClubForm.billingStartDate || undefined,
        billingEndDate: newClubForm.billingEndDate || undefined,
        autoRenew: newClubForm.autoRenew,
      });

      setShowCreateModal(false);
      setNewClubForm({
        name: "",
        location: "",
        adminEmail: "",
        adminName: "",
        accessCode: "",
        subscriptionPlanId: "",
        billingStartDate: "",
        billingEndDate: "",
        autoRenew: true,
      });
      toast.success("Club created and invitation sent!");
      loadClubs();
    } catch (error) {
      toast.error(error.message || "Failed to create club");
    }

    setCreating(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "active":
        return "green";
      case "inactive":
        return "red";
      default:
        return "gray";
    }
  };

  const getClubAdmins = (club) => {
    return club.users?.filter((user) => user.role === "club_admin") || [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Club Management</h1>
          <p className="text-gray-600">
            View and manage all clubs in the system
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            {filteredClubs.length} of {clubs.length} clubs
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <PlusIcon className="h-4 w-4 mr-1" />
            Create Club
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by club name, location, or admin name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Clubs List */}
      <div className="grid grid-cols-1 gap-6">
        {filteredClubs.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {searchTerm ? "No clubs found" : "No clubs yet"}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm
                  ? "Try adjusting your search terms"
                  : "Clubs will appear here once they are created"}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredClubs.map((club) => {
            const admins = getClubAdmins(club);
            return (
              <Card key={club.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {club.name}
                        </h3>
                        <Badge variant={getStatusBadgeVariant(club.status)}>
                          {club.status}
                        </Badge>
                        {club.subscription_plan && (
                          <Badge variant="blue">{club.subscription_plan}</Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div className="space-y-2">
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPinIcon className="h-4 w-4 mr-2" />
                            {club.location || "No location"}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <CalendarIcon className="h-4 w-4 mr-2" />
                            Created {formatDate(club.created_at)}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center text-sm text-gray-600">
                            <UsersIcon className="h-4 w-4 mr-2" />
                            {club.player_count || 0} players
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                            {club.active_evaluations || 0} active evaluations
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="text-sm">
                            <span className="text-gray-600">Admins:</span>
                            {admins.length > 0 ? (
                              <div className="mt-1">
                                {admins.map((admin, index) => (
                                  <div
                                    key={admin.email || index}
                                    className="text-gray-900"
                                  >
                                    {admin.name} ({admin.email})
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-gray-500 mt-1">
                                No admins
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {club.subscription_plan && (
                        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center text-sm">
                            <CreditCardIcon className="h-4 w-4 mr-2 text-slate-600" />
                            <span className="text-slate-900 font-medium">
                              {club.subscription_plan}
                            </span>
                            <span className="text-slate-700 ml-2">
                              {formatDate(club.billing_start_date)} -{" "}
                              {formatDate(club.billing_end_date)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenClub(club)}
                      >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
                        Open
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(club)}
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditClub(club)}
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Club Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Club Details"
        size="lg"
      >
        {selectedClub && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Basic Information
                </h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>{" "}
                    <span className="text-gray-900">{selectedClub.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Location:</span>{" "}
                    <span className="text-gray-900">
                      {selectedClub.location || "Not specified"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Access Code:</span>{" "}
                    <span className="text-gray-900 font-mono">
                      {selectedClub.access_code}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>{" "}
                    <Badge variant={getStatusBadgeVariant(selectedClub.status)}>
                      {selectedClub.status}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-gray-600">Created:</span>{" "}
                    <span className="text-gray-900">
                      {formatDate(selectedClub.created_at)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Statistics
                </h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">Players:</span>{" "}
                    <span className="text-gray-900">
                      {selectedClub.player_count || 0}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Staff:</span>{" "}
                    <span className="text-gray-900">
                      {selectedClub.users?.length || 0}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Active Evaluations:</span>{" "}
                    <span className="text-gray-900">
                      {selectedClub.active_evaluations || 0}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">
                      Completed Evaluations:
                    </span>{" "}
                    <span className="text-gray-900">
                      {selectedClub.completed_evaluations || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Information */}
            {selectedClub.subscription_plan && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Subscription
                </h4>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Plan:</span>{" "}
                      <span className="text-slate-900 font-medium">
                        {selectedClub.subscription_plan}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Auto Renew:</span>{" "}
                      <span className="text-gray-900">
                        {selectedClub.auto_renew ? "Yes" : "No"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Start Date:</span>{" "}
                      <span className="text-gray-900">
                        {formatDate(selectedClub.billing_start_date)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">End Date:</span>{" "}
                      <span className="text-gray-900">
                        {formatDate(selectedClub.billing_end_date)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Staff Information */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Staff Members
              </h4>
              {selectedClub.users && selectedClub.users.length > 0 ? (
                <div className="space-y-2">
                  {selectedClub.users.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {user.email}
                        </div>
                      </div>
                      <Badge variant="gray">
                        {user.role.replace("_", " ")}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No staff members</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Create Club Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Club"
        size="lg"
      >
        <form onSubmit={handleCreateClub} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Club Name <span className="text-red-500">*</span>
              </label>
              <Input
                name="name"
                placeholder="e.g. Riverside Hockey Club"
                value={newClubForm.name}
                onChange={handleCreateInputChange}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location <span className="text-red-500">*</span>
              </label>
              <Input
                name="location"
                placeholder="e.g. Calgary, AB"
                value={newClubForm.location}
                onChange={handleCreateInputChange}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admin Name <span className="text-red-500">*</span>
              </label>
              <Input
                name="adminName"
                placeholder="e.g. Jane Smith"
                value={newClubForm.adminName}
                onChange={handleCreateInputChange}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admin Email <span className="text-red-500">*</span>
              </label>
              <Input
                name="adminEmail"
                type="email"
                placeholder="e.g. jane@club.com"
                value={newClubForm.adminEmail}
                onChange={handleCreateInputChange}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Access Code <span className="text-red-500">*</span>
            </label>
            <div className="flex space-x-2">
              <Input
                name="accessCode"
                placeholder="e.g. RHC2026"
                value={newClubForm.accessCode}
                onChange={handleCreateInputChange}
                required
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={generateAccessCode}
              >
                Generate
              </Button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Players use this code to join evaluations
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subscription Plan
            </label>
            <select
              name="subscriptionPlanId"
              value={newClubForm.subscriptionPlanId}
              onChange={handleCreateInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            >
              <option value="">No subscription plan</option>
              {subscriptionPlans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} - ${plan.price_per_player_per_year}/player per year
                </option>
              ))}
            </select>
          </div>

          {newClubForm.subscriptionPlanId && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing Start Date
                  </label>
                  <Input
                    name="billingStartDate"
                    type="date"
                    value={newClubForm.billingStartDate}
                    onChange={handleCreateInputChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing End Date (Optional)
                  </label>
                  <Input
                    name="billingEndDate"
                    type="date"
                    value={newClubForm.billingEndDate}
                    onChange={handleCreateInputChange}
                  />
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="createAutoRenew"
                  name="autoRenew"
                  checked={newClubForm.autoRenew}
                  onChange={handleCreateInputChange}
                  className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="createAutoRenew"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Auto-renew subscription
                </label>
              </div>
            </>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-sm text-blue-800">
              An invitation email will be sent to the admin with their login
              credentials.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                creating ||
                !newClubForm.name ||
                !newClubForm.location ||
                !newClubForm.adminEmail ||
                !newClubForm.adminName ||
                !newClubForm.accessCode
              }
            >
              {creating ? "Creating..." : "Create Club"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Club Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Club"
        size="lg"
      >
        {editingClub && (
          <form onSubmit={handleUpdateClub} className="space-y-4">
            <Input
              name="name"
              placeholder="Club Name"
              value={editingClub.name}
              onChange={handleEditInputChange}
              required
            />

            <Input
              name="location"
              placeholder="Location"
              value={editingClub.location}
              onChange={handleEditInputChange}
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription Plan
              </label>
              <select
                name="subscriptionPlanId"
                value={editingClub.subscriptionPlanId}
                onChange={handleEditInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              >
                <option value="">No subscription plan</option>
                {subscriptionPlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} - ${plan.price_per_player_per_year}/player per
                    year
                  </option>
                ))}
              </select>
            </div>

            {editingClub.subscriptionPlanId && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billing Start Date
                  </label>
                  <Input
                    name="billingStartDate"
                    type="date"
                    value={editingClub.billingStartDate}
                    onChange={handleEditInputChange}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billing End Date (Optional)
                  </label>
                  <Input
                    name="billingEndDate"
                    type="date"
                    value={editingClub.billingEndDate}
                    onChange={handleEditInputChange}
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="editAutoRenew"
                    name="autoRenew"
                    checked={editingClub.autoRenew}
                    onChange={(e) =>
                      setEditingClub({
                        ...editingClub,
                        autoRenew: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="editAutoRenew"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    Auto-renew subscription
                  </label>
                </div>
              </>
            )}

            <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-slate-900 mb-1">Note</h4>
              <p className="text-sm text-slate-800">
                Changes to the subscription plan will take effect immediately.
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updating || !editingClub.name}>
                {updating ? "Updating..." : "Update Club"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default ClubManagement;
